import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrderInfosResponse } from '~/api/queries/order'
import {
  type ApiResponseError,
  fetchApiWithToken,
  getApiViolations
} from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import type { AddressPayload } from '~/types/global'
import { toast } from '~/utils'
import { buildOrderContractFields } from '../utils'

type CreateOrderPayload = {
  contact: string
  qualifications: string[]
  specialities?: string[]
  segmentations?: string[]
  jobTitle?: string
  address?: AddressPayload
  locationRadius?: number
  applicantWantedCount: number
  contractTerm?: number | null
  contractStart?: string | null
  contractEnd?: string | null
}

export const useCreateOrderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<OrderInfosResponse, ApiResponseError, CreateOrderPayload>({
    mutationFn: async ({
      contact,
      qualifications,
      specialities,
      segmentations,
      jobTitle,
      address,
      locationRadius,
      applicantWantedCount,
      contractTerm,
      contractStart,
      contractEnd
    }) => {
      const payload: any = {
        contact,
        qualifications: qualifications.map(
          (q) => `/v1/taxonomies/qualifications/${q}`
        ),
        status: 1,
        applicantWantedCount: applicantWantedCount ?? 0,
        ...buildOrderContractFields({
          jobTitle,
          address,
          contractTerm,
          contractStart,
          contractEnd
        })
      }

      if (specialities?.length) {
        payload.specialities = specialities.map(
          (s) => `/v1/taxonomies/specialities/${s}`
        )
      }

      if (segmentations?.length) {
        payload.segmentations = segmentations.map(
          (s) => `/v1/taxonomies/segmentations/${s}`
        )
      }

      if (locationRadius) {
        payload.locationRadius = locationRadius
      }

      return fetchApiWithToken<OrderInfosResponse>({
        method: 'post',
        url: '/v1/orders',
        data: payload
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast(
        'Commande créée',
        'La commande a été créée avec succès',
        ToastType.Success
      )
    },
    onError: (error) => {
      if (getApiViolations(error).length > 0) {
        return
      }
      toast(
        'Erreur lors de la création',
        'Une erreur est survenue lors de la création de la commande',
        ToastType.Danger
      )
    }
  })
}
